/**
 * Multi-Select Checkbox Component
 * Simple checkbox list for selecting multiple items
 */

import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';

interface MultiSelectOption {
  _id: string;
  name: string;
}

interface MultiSelectCheckboxProps {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isLoading?: boolean;
  emptyMessage?: string;
}

export function MultiSelectCheckbox({
  label,
  options,
  selected,
  onChange,
  isLoading,
  emptyMessage = 'No options available',
}: MultiSelectCheckboxProps) {
  const handleToggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      ) : options.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">{emptyMessage}</p>
      ) : (
        <div className="border rounded-md p-3 max-h-[200px] overflow-y-auto space-y-2">
          {options.map((option) => (
            <div key={option._id} className="flex items-center gap-2">
              <Checkbox
                id={`${label}-${option._id}`}
                checked={selected.includes(option._id)}
                onCheckedChange={() => handleToggle(option._id)}
              />
              <label
                htmlFor={`${label}-${option._id}`}
                className="text-sm cursor-pointer flex-1"
              >
                {option.name}
              </label>
            </div>
          ))}
        </div>
      )}
      {!isLoading && selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selected.length} selected
        </p>
      )}
    </div>
  );
}
